import { Store, select } from '@ngrx/store';
import {
  EventTypes,
  LoginResponse,
  LogoutAuthOptions,
  OidcSecurityService,
  OpenIdConfiguration,
  PublicEventsService,
} from 'angular-auth-oidc-client';

import { DOCUMENT, Location as NgLocation } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import { oidcActions, userActions, userReducers } from '../@store';
import { AuthModuleConfigId } from '../enums/auth-module-config-id';
import { CompanyViewModel, CustomParams } from '../models';
import { MwAppConfigService } from './../../config/config.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({ providedIn: 'root' })
export class MwAuthService {
  private authChecked = false;

  oidcConfigLoaded$ = this.eventService.registerForEvents().pipe(
    filter((value) => value.type === EventTypes.ConfigLoaded),
    take(1)
  );

  get userData$(): Observable<any> {
    return this.oidcService.userData$.pipe(
      map(
        (userData) =>
          userData.allUserData.find(
            (t) => t.configId === this.currentConfigId.value
          )?.userData
      )
    );
  }

  get loggedIn$(): Observable<boolean> {
    return this.oidcService.isAuthenticated$.pipe(
      map(
        (result) =>
          result.allConfigsAuthenticated.find(
            (t) => t.configId === this.currentConfigId.value
          )?.isAuthenticated || false
      )
    );
  }

  get activeCompany$(): Observable<CompanyViewModel | null> {
    return this.store.select(userReducers.companyDataIsReady).pipe(
      filter((isReady) => isReady),
      switchMap(() => this.store.pipe(select(userReducers.getActiveCompany)))
    );
  }

  private get windowInternal(): Window | null {
    return this.document.defaultView;
  }

  private currentConfigId: BehaviorSubject<AuthModuleConfigId>;
  private currentOpenIDConfiguration: OpenIdConfiguration | null = null;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly location: NgLocation,
    private readonly store: Store<any>,
    private readonly oidcService: OidcSecurityService,
    private readonly eventService: PublicEventsService,
    private readonly router: Router,
    private readonly configService: MwAppConfigService
  ) {
    this.checkAuthOnRouteChange();

    this.currentConfigId = new BehaviorSubject<AuthModuleConfigId>(
      this.getConfigurationId()
    );
    this.currentConfigId
      .pipe(
        switchMap((configId) => this.oidcService.getConfiguration(configId))
      )
      .subscribe(
        (openIdConfiguration) =>
          (this.currentOpenIDConfiguration = openIdConfiguration)
      );
  }

  /**
   * Returns the access token for the login scenario.
   *
   * @param configId The configId to check the information for. If not passed, the first configs will be taken
   *
   * @returns A string with the access token.
   */
  getAccessToken(configId?: AuthModuleConfigId): Observable<string> {
    return this.oidcService.getAccessToken(
      configId ?? this.currentConfigId.value
    );
  }

  /**
   * Returns the payload from the access token as an Observable
   */
  getPayloadFromAccessToken(): Observable<any> {
    return this.oidcService.getPayloadFromAccessToken(
      false,
      this.currentConfigId.value
    );
  }
  isDevincoConnectAccessToken(): Observable<boolean> {
    return this.getPayloadFromAccessToken().pipe(
      take(1),
      map((claims) => {
        const iss: string = claims?.iss || '';
        return iss.toLocaleLowerCase().endsWith('devinco.com');
      })
    );
  }
  /**
   * Get active configurationId
   * @returns AuthModuleConfigId
   */
  getConfigurationId(): AuthModuleConfigId {
    const persistedConfigurationId =
      LocalStorageService.getCurrentOIDCConfigurationId();
    const defaultConfigurationId =
      this.configService.getInstantConfig()?.environment?.production === true
        ? AuthModuleConfigId.MobileWorker
        : AuthModuleConfigId.DevincoConnect;

    if (this.isLegacyUrl()) {
      return AuthModuleConfigId.MobileWorker;
    }

    return persistedConfigurationId &&
      Object.values(AuthModuleConfigId).find(
        (t) => t === persistedConfigurationId
      )
      ? (persistedConfigurationId as AuthModuleConfigId)
      : defaultConfigurationId;
  }

  /**
   * Creates the authorize URL based on your flow
   * @param configurationId  The configId to perform the action in behalf of. If not passed, the first configs will be taken
   * @returns A string with the authorize URL or null
   */
  getAuthorizeUrl(
    configurationId: AuthModuleConfigId
  ): Observable<string | null> {
    return this.oidcService.getAuthorizeUrl(undefined, configurationId);
  }

  /**
   *  Change active configuration
   * @param configurationId AuthModuleConfigId
   */
  changeCurrentConfigurationId(configurationId: AuthModuleConfigId): void {
    this.currentConfigId.next(configurationId);
    LocalStorageService.setCurrentOIDCConfigurationId(configurationId);
  }

  /**
   * Call logout action `[User] Log out`
   */
  logout(): void {
    this.store.dispatch(userActions.logOut());
  }

  /**
   * Logs out on the server and the local client. If the server state has changed, confirmed via check session,
   * then only a local logout is performed.
   */
  logoff(
    configId?: AuthModuleConfigId,
    logoutAuthOptions?: LogoutAuthOptions
  ): Observable<unknown> {
    return this.oidcService.logoff(
      configId ?? this.currentConfigId.value,
      logoutAuthOptions
    );
  }

  /**
   * Logs the user out of the application without logging them out of the server.
   * Use this method if you have _one_ config enabled.
   */
  logoffLocal(configId?: AuthModuleConfigId): void {
    this.oidcService.logoffLocal(configId ?? this.currentConfigId.value);
  }

  /**
   * Starts the complete setup flow for multiple configurations.
   * Calling will start the entire authentication flow, and the returned observable
   * will denote whether the user was successfully authenticated including the user data, the access token, the configId and
   * an error message in case an error happened in an array for each config which was provided
   *
   * @returns An array of `LoginResponse` objects containing all information about the logins
   */
  checkAuth(): Observable<LoginResponse> {
    if (this.windowInternal?.opener) {
      // checkAuth will return null if opener will have value
      // https://github.com/damienbod/angular-auth-oidc-client/blob/36fc58da1761f5d243423dd6ad33b98a68526c31/projects/angular-auth-oidc-client/src/lib/auth-state/check-auth.service.ts#L111
      this.windowInternal.opener = null;
    }

    return this.oidcService.checkAuthMultiple().pipe(
      map(
        (responses) =>
          responses.filter((t) => t.configId === this.currentConfigId.value)[0]
      ),
      tap((result) =>
        LocalStorageService.tryToRestoreRoute(
          this.configService.getInstantConfig(),
          this.router,
          !!result
        )
      ),
      catchError((err) => {
        console.error(err);
        this.oidcService.logoffLocal();
        localStorage.clear();
        this.router.navigate(['/']);

        const value: LoginResponse = {
          isAuthenticated: false,
          userData: null,
          accessToken: '',
          idToken: '',
          configId: '',
        };

        return of(value);
      })
    );
  }

  /**
   * Starts the complete setup flow for multiple configurations.
   * Calling will start the entire authentication flow, and the returned observable
   * will denote whether the user was successfully authenticated including the user data, the access token, the configId and
   * an error message in case an error happened in an array for each config which was provided
   * @returns An array of `LoginResponse` objects containing all information about the logins
   */
  checkAuthMultiple(): Observable<LoginResponse[]> {
    return this.oidcService.checkAuthMultiple();
  }

  /**
   * Redirects the user to the Security Token Service to begin the authentication process.
   */
  authorize(redirectUrl?: string, configId?: string): void {
    if (redirectUrl) {
      LocalStorageService.saveStoredRedirectRoute(redirectUrl);
    }

    this.oidcService.authorize(configId ?? this.currentConfigId.value, {
      customParams: this.getCustomParams(),
    });
  }

  /**
   * Redirect user to company selector page
   *
   * @param selectedCompanyId - company ID which by default will be selected in the company selector
   * @param opts  - company selector options
   */
  changeCompany(
    selectedCompanyId?: number,
    opts?: {
      ignoreCompanySelector?: boolean;
      isSaveCurrentRoute?: boolean;
    }
  ): void {
    const companySelectorRoute =
      this.configService.getInstantConfig()?.companySelectorRoute;

    let returnUrl: string | undefined;

    if (opts?.isSaveCurrentRoute) {
      returnUrl = this.saveCurrentRoute();
    }

    const customParams = {
      selectedCompanyId: selectedCompanyId ?? 0,
      changeCompany: true,
      ignoreCompanySelector: false,
    };

    if (opts?.ignoreCompanySelector) {
      customParams.ignoreCompanySelector = true;
    }

    if (this.isUseSTSCompanySelector()) {
      // Legacy STS authorization which support company selection.
      // When migration to DEVINCO CONNECT
      // will be finished this code should be removed
      this.oidcService.authorize(this.currentConfigId.value, {
        customParams,
      });
    } else {
      LocalStorageService.deleteStoredRedirectRoute();
      returnUrl = this.saveCurrentRoute();
      const queryParams = {
        returnUrl,
        selectedCompanyId,
      };

      this.router.navigate([companySelectorRoute || 'company-selector'], {
        queryParams,
      });
    }
  }

  /**
   * Check if STS company selector should be used
   * @returns True - STS company selector should be used
   */
  isUseSTSCompanySelector(): boolean {
    const companySelectorRoute =
      this.configService.getInstantConfig()?.companySelectorRoute;
    const userLocalCompanySelector =
      LocalStorageService.getUseLocalCompanySelector() && companySelectorRoute;

    return !this.isDevincoConnectActive() && !userLocalCompanySelector;
  }

  /**
   * Check if DEVINCO CONNECT is active
   * @returns True - DEVINCO CONNECT is active
   */
  isDevincoConnectActive(): boolean {
    return (
      this.currentOpenIDConfiguration?.authority?.indexOf('devinco.com') !== -1
    );
  }

  /**
   * Check if app is accessed from legacy URL
   * @returns true - legacy url (for example https://hseq-legacy.mworker.com)
   */
  isLegacyUrl(): boolean {
    return this.document.location.href.indexOf('legacy.mworker.com') !== -1;
  }

  /**
   * @deprecated Will not be supported with Devinco Connect
   *
   * @param guid - auto login GUID which will be used for login
   */
  authorizeByGuid(guid: string): void {
    this.oidcService.authorize(this.currentConfigId.value, {
      customParams: {
        autoLoginToken: guid,
        autoLoginCallback: true,
      },
    });
  }

  /**
   * @deprecated Will not be supported with Devinco Connect
   * @param param0
   */
  loginByGuid({ guid, language }: { guid: string; language: string }): void {
    this.store.dispatch(
      userActions.logInByGuid({
        guid,
        language,
      })
    );
  }

  /**
   * @deprecated Will not be supported with Devinco Connect
   *
   * @param data
   */
  authorizeUsingPassword(data: {
    userName: string;
    password: string;
    companyId?: number;
    ignoreCompanySelector?: boolean;
    isSaveCurrentRoute?: boolean;
  }): void {
    const customParams: any = {
      login_hint: data.userName || '',
      password: data.password || '',
    };
    const companyId = data.companyId || 0;

    if (data.isSaveCurrentRoute) {
      this.saveCurrentRoute();
    }

    if (companyId > 0) {
      customParams.companyId = companyId;
    }

    if (data.ignoreCompanySelector) {
      customParams.ignoreCompanySelector = data.ignoreCompanySelector;
    }

    this.oidcService.authorize(undefined, {
      customParams,
    });
  }

  private saveCurrentRoute(): string {
    // If stored route is already set return it as it is
    const queryPart =
      LocalStorageService.getStoredRedirectRoute() || this.location.path();

    // Ignore `no-access` and `unauthorized` routes
    if (queryPart.includes('no-access') || queryPart.includes('unauthorized')) {
      return '/';
    }

    LocalStorageService.saveStoredRedirectRoute(queryPart);

    return queryPart;
  }

  private checkAuthOnRouteChange(): void {
    this.router.events
      .pipe(
        map((e) => e as NavigationStart),
        filter((e) => !!e && !this.authChecked)
      )
      .subscribe((e: NavigationStart) => {
        this.authChecked = true;
        this.store.dispatch(oidcActions.checkAuth());
      });
  }

  private getCustomParams(): CustomParams {
    const urlParams = new URLSearchParams(window.location.search);
    const params: CustomParams = {};

    urlParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }
}
